/**
 * Editor lista modalita pagamento per tenant.
 *
 * Caricata da Impostazioni Azienda > tab Pagamenti.
 * CRUD su /api/v1/config/modalita-pagamento.
 *
 * Solo admin/super_admin (lato backend, qui non c'e' controllo aggiuntivo).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';

interface Modalita {
  id: string;
  codice: string;
  descrizione: string;
  ordinamento: number;
  attiva: boolean;
  /** Traduzione della condizione commerciale nei due domini FatturaPA
   *  (2.4.2.2 ModalitaPagamento e 2.4.1 CondizioniPagamento). Senza queste, la
   *  voce non e' utilizzabile su una fattura elettronica: non si saprebbe cosa
   *  dichiarare allo SDI, e il form di fattura lo segnala. */
  mp_codice: string | null;
  tp_codice: string | null;
}

interface ModalitaDraft {
  codice: string;
  descrizione: string;
  ordinamento: string;
  attiva: boolean;
  mp_codice: string;
  tp_codice: string;
}

/** Voce di uno dei due domini normativi, servita in sola lettura dal backend. */
interface VoceDominio {
  codice: string;
  descrizione: string;
  esponibile_ui: boolean;
}

const emptyDraft = (): ModalitaDraft => ({
  codice: '',
  descrizione: '',
  ordinamento: '0',
  mp_codice: '',
  tp_codice: '',
  attiva: true,
});

export const ModalitaPagamentoEditor: React.FC = () => {
  const token = localStorage.getItem('token') ?? '';
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [lista, setLista] = useState<Modalita[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ModalitaDraft>(emptyDraft());
  const [showNew, setShowNew] = useState(false);

  /* I due domini della norma, in sola lettura: qui si SCEGLIE, non si
   * inventano codici. Serviti dal documentale, che li tiene a database. */
  const [dominioMp, setDominioMp] = useState<VoceDominio[]>([]);
  const [dominioTp, setDominioTp] = useState<VoceDominio[]>([]);
  useEffect(() => {
    void (async () => {
      const res = await fetch('/api/v1/fatture/lookups/fatturapa-pagamento', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const d = await res.json();
        setDominioMp(d.modalita ?? []);
        setDominioTp(d.condizioni ?? []);
      }
    })();
  }, [token]);

  const carica = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/config/modalita-pagamento?solo_attive=false', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data: Modalita[] = await res.json();
      setLista(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { void carica(); }, [carica]);

  const startEdit = (m: Modalita) => {
    setEditId(m.id);
    setDraft({
      codice: m.codice,
      descrizione: m.descrizione,
      ordinamento: String(m.ordinamento),
      attiva: m.attiva,
      mp_codice: m.mp_codice ?? '',
      tp_codice: m.tp_codice ?? '',
    });
    setShowNew(false);
  };

  const cancelEdit = () => {
    setEditId(null);
    setDraft(emptyDraft());
  };

  const startNew = () => {
    setEditId(null);
    setDraft(emptyDraft());
    setShowNew(true);
  };

  const validaDraft = (): string | null => {
    if (!draft.codice.trim()) return 'Codice obbligatorio';
    if (!/^[a-z0-9_]+$/.test(draft.codice)) return 'Codice: solo minuscole, numeri, underscore';
    if (!draft.descrizione.trim()) return 'Descrizione obbligatoria';
    const ord = parseInt(draft.ordinamento, 10);
    if (isNaN(ord)) return 'Ordinamento non valido';
    return null;
  };

  const salvaNuova = async () => {
    const err = validaDraft();
    if (err) { setError(err); return; }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch('/api/v1/config/modalita-pagamento', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          codice: draft.codice.trim(),
          descrizione: draft.descrizione.trim(),
          ordinamento: parseInt(draft.ordinamento, 10),
          attiva: draft.attiva,
          mp_codice: draft.mp_codice || null,
          tp_codice: draft.tp_codice || null,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Errore ${res.status}`);
      }
      setSuccess('Modalita creata');
      setShowNew(false);
      setDraft(emptyDraft());
      await carica();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore creazione');
    } finally {
      setSaving(false);
    }
  };

  const salvaModifica = async () => {
    if (!editId) return;
    const err = validaDraft();
    if (err) { setError(err); return; }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`/api/v1/config/modalita-pagamento/${editId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          codice: draft.codice.trim(),
          descrizione: draft.descrizione.trim(),
          ordinamento: parseInt(draft.ordinamento, 10),
          attiva: draft.attiva,
          mp_codice: draft.mp_codice || null,
          tp_codice: draft.tp_codice || null,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Errore ${res.status}`);
      }
      setSuccess('Modalita aggiornata');
      setEditId(null);
      setDraft(emptyDraft());
      await carica();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  const elimina = async (id: string, descrizione: string) => {
    if (!window.confirm(`Eliminare la modalita "${descrizione}"? I documenti gia' emessi resteranno invariati.`)) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`/api/v1/config/modalita-pagamento/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Errore ${res.status}`);
      }
      setSuccess('Modalita eliminata');
      await carica();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore eliminazione');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-4">Caricamento...</div>;

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-md border p-3" style={{ borderColor: 'var(--color-destructive)', color: 'var(--color-destructive)' }}>
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-md border p-3" style={{ borderColor: 'var(--color-success, #22c55e)', color: 'var(--color-success, #22c55e)' }}>
          {success}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Modalita di Pagamento</span>
            {!showNew && !editId && (
              <Button size="sm" onClick={startNew}>Aggiungi modalita</Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
            Lista delle modalita di pagamento disponibili nei documenti (preventivi, ordini, fatture).
            Le modifiche si applicano ai nuovi documenti: quelli gia' emessi conservano la dicitura originale.
          </p>

          {showNew && (
            <div className="rounded-md border p-4 space-y-3" style={{ borderColor: 'var(--color-primary, #6366f1)' }}>
              <h4 className="text-sm font-semibold">Nuova modalita</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-sm">Codice (slug)</Label>
                  <Input
                    value={draft.codice}
                    onChange={(e) => setDraft((d) => ({ ...d, codice: e.target.value }))}
                    placeholder="es. bonifico_45_gg"
                    maxLength={50}
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-sm">Descrizione (mostrata)</Label>
                  <Input
                    value={draft.descrizione}
                    onChange={(e) => setDraft((d) => ({ ...d, descrizione: e.target.value }))}
                    placeholder="es. Bonifico 45 gg DFFM"
                    maxLength={200}
                  />
                </div>
              </div>
              {/* Traduzione nei codici della fattura elettronica. Senza questi due,
                  la condizione non e' utilizzabile su una fattura: il form di
                  fattura la mostra come "da completare" e non la lascia usare. */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-sm">Modalita' FatturaPA</Label>
                  <select
                    className="w-full rounded border bg-background text-foreground px-3 py-2 text-sm"
                    value={draft.mp_codice}
                    onChange={(e) => setDraft((d) => ({ ...d, mp_codice: e.target.value }))}
                  >
                    <option value="">- non impostata -</option>
                    {dominioMp.map((v) => (
                      <option key={v.codice} value={v.codice}>
                        {v.codice} - {v.descrizione}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Condizioni FatturaPA</Label>
                  <select
                    className="w-full rounded border bg-background text-foreground px-3 py-2 text-sm"
                    value={draft.tp_codice}
                    onChange={(e) => setDraft((d) => ({ ...d, tp_codice: e.target.value }))}
                  >
                    <option value="">- non impostate -</option>
                    {dominioTp.map((v) => (
                      <option key={v.codice} value={v.codice} disabled={!v.esponibile_ui}>
                        {v.codice} - {v.descrizione}
                        {v.esponibile_ui ? '' : ' (non ancora emettibile)'}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 items-end">
                <div className="space-y-1">
                  <Label className="text-sm">Ordinamento</Label>
                  <Input
                    type="number"
                    value={draft.ordinamento}
                    onChange={(e) => setDraft((d) => ({ ...d, ordinamento: e.target.value }))}
                  />
                </div>
                <div className="flex items-center gap-2 pb-2">
                  <input
                    type="checkbox"
                    id="new-attiva"
                    checked={draft.attiva}
                    onChange={(e) => setDraft((d) => ({ ...d, attiva: e.target.checked }))}
                  />
                  <Label htmlFor="new-attiva" className="text-sm cursor-pointer">Attiva</Label>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" onClick={() => { setShowNew(false); setDraft(emptyDraft()); }}>
                    Annulla
                  </Button>
                  <Button onClick={salvaNuova} disabled={saving}>
                    {saving ? 'Salvataggio...' : 'Crea'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {lista.length === 0 && !showNew && (
              <p className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
                Nessuna modalita configurata. Clicca "Aggiungi modalita" per crearne una.
              </p>
            )}
            {lista.map((m) => (
              <div
                key={m.id}
                className="rounded-md border p-3"
                style={!m.attiva ? { opacity: 0.55 } : undefined}
              >
                {editId === m.id ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-sm">Codice</Label>
                        <Input
                          value={draft.codice}
                          onChange={(e) => setDraft((d) => ({ ...d, codice: e.target.value }))}
                          maxLength={50}
                        />
                      </div>
                      <div className="col-span-2 space-y-1">
                        <Label className="text-sm">Descrizione</Label>
                        <Input
                          value={draft.descrizione}
                          onChange={(e) => setDraft((d) => ({ ...d, descrizione: e.target.value }))}
                          maxLength={200}
                        />
                      </div>
                    </div>
                    {/* Traduzione nei codici della fattura elettronica. Senza questi due,
                        la condizione non e' utilizzabile su una fattura: il form di
                        fattura la mostra come "da completare" e non la lascia usare. */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-sm">Modalita' FatturaPA</Label>
                        <select
                          className="w-full rounded border bg-background text-foreground px-3 py-2 text-sm"
                          value={draft.mp_codice}
                          onChange={(e) => setDraft((d) => ({ ...d, mp_codice: e.target.value }))}
                        >
                          <option value="">- non impostata -</option>
                          {dominioMp.map((v) => (
                            <option key={v.codice} value={v.codice}>
                              {v.codice} - {v.descrizione}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Condizioni FatturaPA</Label>
                        <select
                          className="w-full rounded border bg-background text-foreground px-3 py-2 text-sm"
                          value={draft.tp_codice}
                          onChange={(e) => setDraft((d) => ({ ...d, tp_codice: e.target.value }))}
                        >
                          <option value="">- non impostate -</option>
                          {dominioTp.map((v) => (
                            <option key={v.codice} value={v.codice} disabled={!v.esponibile_ui}>
                              {v.codice} - {v.descrizione}
                              {v.esponibile_ui ? '' : ' (non ancora emettibile)'}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-end">
                      <div className="space-y-1">
                        <Label className="text-sm">Ordinamento</Label>
                        <Input
                          type="number"
                          value={draft.ordinamento}
                          onChange={(e) => setDraft((d) => ({ ...d, ordinamento: e.target.value }))}
                        />
                      </div>
                      <div className="flex items-center gap-2 pb-2">
                        <input
                          type="checkbox"
                          id={`attiva-${m.id}`}
                          checked={draft.attiva}
                          onChange={(e) => setDraft((d) => ({ ...d, attiva: e.target.checked }))}
                        />
                        <Label htmlFor={`attiva-${m.id}`} className="text-sm cursor-pointer">Attiva</Label>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="ghost" onClick={cancelEdit}>Annulla</Button>
                        <Button onClick={salvaModifica} disabled={saving}>
                          {saving ? 'Salvataggio...' : 'Salva'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-xs font-mono" style={{ color: 'var(--color-muted-foreground)' }}>
                        #{m.ordinamento}
                      </span>
                      <span className="font-medium">{m.descrizione}</span>
                      <Badge variant="outline">{m.codice}</Badge>
                      {!m.attiva && <Badge variant="secondary">disattiva</Badge>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => startEdit(m)}>
                        Modifica
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => elimina(m.id, m.descrizione)}
                        style={{ color: 'var(--color-destructive)' }}
                      >
                        Elimina
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ModalitaPagamentoEditor;
